import React, { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function VideoPlayer({ file, videoUrl, mimeType }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Reset loading state when file/videoUrl changes
    setIsLoading(true);
    setHasError(false);

    // If videoUrl is provided (from S3), use it directly
    if (videoUrl) {
      setUrl(videoUrl);
      return;
    }

    // Otherwise, create object URL from file
    if (!file) {
      setUrl(null);
      setIsLoading(false);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file, videoUrl]);

  const handleLoadedMetadata = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (!url) return null;

  return (
    <div style={{ marginTop: 12, marginBottom: 24, position: 'relative' }}>
      {/* Loading Placeholder */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            zIndex: 2,
            gap: 12
          }}
        >
          <Loader2 
            size={32} 
            style={{ 
              color: '#3b82f6',
              animation: 'spin 1s linear infinite',
              display: 'inline-block'
            }} 
          />
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
              {t('uploadVideo.loadingVideo', 'Loading video...')}
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#64748b' }}>
              {t('uploadVideo.preparingPlayer', 'Preparing video player')}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {hasError && !isLoading && (
        <div 
          style={{
            padding: 24,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 12,
            textAlign: 'center'
          }}
        >
          <p style={{ margin: 0, fontSize: 14, color: '#dc2626' }}>
            {t('uploadVideo.loadError', 'Failed to load video. Please try again.')}
          </p>
        </div>
      )}

      {/* Video Element */}
      <video
        ref={videoRef}
        src={url}
        controls
        aria-label="Video player"
        style={{ 
          width: '100%', 
          maxWidth: '100%', 
          borderRadius: 12, 
          border: '1px solid var(--border-light)',
          display: isLoading ? 'none' : 'block',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onError={handleError}
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

